import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useProfile } from "../../../hooks/queries/useProfile";
import ProfileCompletenessBar from "../components/profile/ProfileCompletenessBar";
import ProfileHeader from "../components/profile/ProfileHeader";
import PersonalInfoSection from "../components/profile/PersonalInfoSection";
import IDCaptureSection from "../components/profile/IDCaptureSection";
import AvailabilitySection from "../components/profile/AvailabilitySection";
import WorkPreferencesSection from "../components/profile/WorkPreferencesSection";
import WorkHistorySection from "../components/profile/WorkHistorySection";

interface CandidateProfilePageProps {
  onBack: () => void;
}

type ProfileTab = "personal" | "id" | "availability" | "preferences" | "work-history";

const TABS: { key: ProfileTab; label: string }[] = [
  { key: "personal", label: "Personal Info" },
  { key: "id", label: "ID Verification" },
  { key: "availability", label: "Availability" },
  { key: "preferences", label: "Preferences" },
  { key: "work-history", label: "Work History" },
];

export default function CandidateProfilePage({ onBack }: CandidateProfilePageProps) {
  const { data: profile, isLoading, isError } = useProfile();
  const [activeTab, setActiveTab] = useState<ProfileTab>("personal");

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-base text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-900 mb-2">Unable to Load Profile</p>
          <p className="text-base text-gray-500 mb-6">Please try again later.</p>
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-semibold text-[#0156FF] border border-[#0156FF] rounded-full hover:bg-blue-50 transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3 sticky top-0 z-10">
        <button
          type="button"
          onClick={onBack}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h1
          className="text-xl font-extrabold text-gray-900"
          style={{ fontFamily: "Nunito, sans-serif" }}
        >
          My Profile
        </h1>
      </header>

      <div className="px-4 py-5 max-w-2xl mx-auto space-y-4">
        {/* Completeness Bar */}
        <ProfileCompletenessBar profile={profile} />

        {/* Profile Header (photo + name) */}
        <ProfileHeader profile={profile} />

        {/* Section Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-none">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex-shrink-0 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? "text-[#0156FF] border-b-2 border-[#0156FF]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "personal" && <PersonalInfoSection profile={profile} />}
        {activeTab === "id" && <IDCaptureSection profile={profile} />}
        {activeTab === "availability" && <AvailabilitySection profile={profile} />}
        {activeTab === "preferences" && <WorkPreferencesSection profile={profile} />}
        {activeTab === "work-history" && <WorkHistorySection profile={profile} />}
      </div>
    </div>
  );
}
